import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useI18n } from '../../../i18n';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker - use unpkg which has all npm versions
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PdfViewerProps {
    fileUrl: string;
    highlightQuotes?: Array<{
        quote: string;
        id: string;
    }>;
    scrollToQuote?: string;
    onScrollComplete?: () => void;
}

// Normalize text for matching
const normalizeText = (text: string): string => {
    return text
        .replace(/[\r\n\t]+/g, ' ')
        .replace(/\s+/g, ' ')
        .replace(/[""]/g, '"')
        .replace(/['']/g, "'")
        .replace(/[–-]/g, '-')
        .trim()
        .toLowerCase();
};

const ZOOM_STEP = 0.25;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const DEFAULT_ZOOM = 1;

export const PdfViewer: React.FC<PdfViewerProps> = ({ fileUrl, highlightQuotes = [], scrollToQuote, onScrollComplete }) => {
    const { t } = useI18n();
    const [numPages, setNumPages] = useState<number>(0);
    const [baseWidth, setBaseWidth] = useState<number>(800);
    const [zoom, setZoom] = useState<number>(DEFAULT_ZOOM);
    const [error, setError] = useState<string | null>(null);
    const [renderedPages, setRenderedPages] = useState<Set<number>>(new Set());
    const lastScrollQuoteRef = useRef<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const pageWidth = baseWidth * zoom;

    useEffect(() => {
        const updateBaseWidth = () => {
            const container = document.getElementById('pdf-container');
            if (container) {
                setBaseWidth(Math.min(container.clientWidth - 64, 800));
            }
        };

        updateBaseWidth();
        window.addEventListener('resize', updateBaseWidth);
        return () => window.removeEventListener('resize', updateBaseWidth);
    }, []);

    const handleZoomIn = useCallback(() => {
        setZoom(prev => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
    }, []);

    const handleZoomOut = useCallback(() => {
        setZoom(prev => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
    }, []);

    const handleResetZoom = useCallback(() => {
        setZoom(DEFAULT_ZOOM);
    }, []);

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
        setError(null);
        setRenderedPages(new Set());
    };

    const onDocumentLoadError = (error: Error) => {
        setError(error.message);
    };

    // Apply highlighting to a specific page
    const applyHighlightingToPage = useCallback((pageNumber: number) => {
        const pageContainer = document.querySelector(`[data-page-number="${pageNumber}"]`);
        if (!pageContainer) return;

        const textLayer = pageContainer.querySelector('.react-pdf__Page__textContent');
        if (!textLayer) return;

        // Clear existing highlights
        const existingHighlights = textLayer.querySelectorAll('.source-highlight');
        existingHighlights.forEach(el => {
            el.classList.remove('source-highlight');
            el.removeAttribute('data-source-id');
            el.removeAttribute('data-quote');
        });

        if (highlightQuotes.length === 0) return;

        // Get all text spans
        const textSpans = Array.from(textLayer.querySelectorAll('span'));
        const fullPageText = textSpans.map(s => s.textContent || '').join(' ');
        const normalizedPageText = normalizeText(fullPageText);

        for (const { quote, id } of highlightQuotes) {
            if (!quote || !quote.trim()) continue;

            const normalizedQuote = normalizeText(quote);

            // Check if this page contains any part of the quote
            if (!normalizedPageText.includes(normalizedQuote.substring(0, Math.min(20, normalizedQuote.length)))) {
                // Try word-based matching as fallback
                const quoteWords = normalizedQuote.split(' ').filter(w => w.length > 3);
                const hasMatchingWords = quoteWords.some(word => normalizedPageText.includes(word));
                if (!hasMatchingWords) continue;
            }

            // Highlight spans that contain parts of the quote
            textSpans.forEach((span) => {
                const spanText = span.textContent || '';
                const normalizedSpanText = normalizeText(spanText);

                if (!normalizedSpanText) return;

                // Check various matching conditions
                const isMatch =
                    // Exact or partial containment
                    normalizedQuote.includes(normalizedSpanText) ||
                    normalizedSpanText.includes(normalizedQuote) ||
                    // Word-based matching for longer quotes
                    normalizedQuote.split(' ').filter(w => w.length > 3).some(word =>
                        normalizedSpanText.includes(word)
                    ) ||
                    // Check if span words appear in quote
                    normalizedSpanText.split(' ').filter(w => w.length > 3).some(word =>
                        normalizedQuote.includes(word)
                    );

                if (isMatch) {
                    span.classList.add('source-highlight');
                    span.setAttribute('data-source-id', id);
                    span.setAttribute('data-quote', quote);
                }
            });
        }
    }, [highlightQuotes]);

    // Handle scrolling when all pages are rendered
    useEffect(() => {
        if (!scrollToQuote || scrollToQuote === lastScrollQuoteRef.current) return;
        if (renderedPages.size < numPages || numPages === 0) return;

        lastScrollQuoteRef.current = scrollToQuote;
        const normalizedScrollQuote = normalizeText(scrollToQuote);

        // Small delay to ensure highlights are applied
        setTimeout(() => {
            const container = containerRef.current;
            if (!container) {
                onScrollComplete?.();
                return;
            }

            // Find a highlight that matches the scroll quote
            const highlights = container.querySelectorAll('.source-highlight');
            let targetElement: Element | null = null;

            for (const highlight of highlights) {
                const highlightQuote = highlight.getAttribute('data-quote') || '';
                const normalizedHighlightQuote = normalizeText(highlightQuote);

                if (normalizedHighlightQuote === normalizedScrollQuote ||
                    normalizedHighlightQuote.includes(normalizedScrollQuote) ||
                    normalizedScrollQuote.includes(normalizedHighlightQuote)) {
                    targetElement = highlight;
                    break;
                }
            }

            // Fallback: find any highlight
            if (!targetElement && highlights.length > 0) {
                targetElement = highlights[0];
            }

            if (targetElement) {
                targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                targetElement.classList.add('scroll-flash');
                setTimeout(() => targetElement?.classList.remove('scroll-flash'), 1500);
            }

            onScrollComplete?.();
        }, 200);
    }, [scrollToQuote, renderedPages, numPages, onScrollComplete]);

    // Reset scroll tracking
    useEffect(() => {
        if (!scrollToQuote) {
            lastScrollQuoteRef.current = null;
        }
    }, [scrollToQuote]);

    // Re-apply highlighting when quotes change
    useEffect(() => {
        if (numPages === 0) return;

        const timeout = setTimeout(() => {
            for (let i = 1; i <= numPages; i++) {
                applyHighlightingToPage(i);
            }
        }, 150);

        return () => clearTimeout(timeout);
    }, [highlightQuotes, numPages, applyHighlightingToPage]);

    const onPageRenderSuccess = useCallback((pageNumber: number) => {
        setTimeout(() => {
            applyHighlightingToPage(pageNumber);
            setRenderedPages(prev => new Set(prev).add(pageNumber));
        }, 100);
    }, [applyHighlightingToPage]);

    if (error) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-red-500">{t.playbooks.viewer.errorLoadingPdf(error)}</div>
            </div>
        );
    }

    return (
        <div id="pdf-container" ref={containerRef} className="h-full flex flex-col bg-gray-100">
            <div className="flex-1 overflow-auto p-8">
                <div className="max-w-4xl mx-auto">
                <Document
                    file={fileUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading={
                        <div className="flex items-center justify-center h-64">
                            <div className="text-gray-500">{t.playbooks.viewer.loadingPdf}</div>
                        </div>
                    }
                >
                    {Array.from(new Array(numPages), (el, index) => {
                        const pageNumber = index + 1;

                        return (
                            <div
                                key={`page_${pageNumber}`}
                                className="mb-4 shadow-lg"
                                data-page-number={pageNumber}
                            >
                                <div className="bg-white p-2">
                                    <div className="text-xs text-gray-500 mb-2">
                                        {t.playbooks.viewer.pageOf(pageNumber, numPages)}
                                    </div>
                                    <Page
                                        pageNumber={pageNumber}
                                        width={pageWidth}
                                        renderTextLayer={true}
                                        renderAnnotationLayer={true}
                                        onRenderSuccess={() => onPageRenderSuccess(pageNumber)}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </Document>
                </div>
            </div>

            {/* Zoom Controls */}
            <div className="flex-shrink-0 border-t bg-white px-4 py-2 flex items-center justify-center gap-2">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleZoomOut}
                    disabled={zoom <= MIN_ZOOM}
                    title={t.playbooks.viewer.zoomOut}
                >
                    <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground min-w-[60px] text-center">
                    {Math.round(zoom * 100)}%
                </span>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleZoomIn}
                    disabled={zoom >= MAX_ZOOM}
                    title={t.playbooks.viewer.zoomIn}
                >
                    <ZoomIn className="h-4 w-4" />
                </Button>
                {zoom !== DEFAULT_ZOOM && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleResetZoom}
                        title={t.playbooks.viewer.resetZoom}
                    >
                        <RotateCcw className="h-4 w-4" />
                    </Button>
                )}
            </div>

            <style>{`
                .react-pdf__Page__textContent .source-highlight {
                    background-color: rgba(254, 240, 138, 0.5) !important;
                    border-radius: 2px;
                    transition: background-color 0.2s;
                    mix-blend-mode: multiply;
                }
                .react-pdf__Page__textContent .source-highlight:hover {
                    background-color: rgba(253, 224, 71, 0.6) !important;
                }
                .react-pdf__Page__textContent .source-highlight.scroll-flash {
                    animation: flash 1.5s ease-out;
                }
                @keyframes flash {
                    0%, 50% { background-color: rgba(249, 115, 22, 0.6) !important; }
                    100% { background-color: rgba(254, 240, 138, 0.5) !important; }
                }
            `}</style>
        </div>
    );
};
